/**
 * Installing the app (#1332).
 *
 * ⚠️ THE PROMPT WAITS UNTIL THE PRODUCT HAS DONE SOMETHING. Asking on first
 * load, before anybody has a plan, is how an install gets declined — and on
 * Android a declined `beforeinstallprompt` is not offered again for months.
 * So the event is captured and held, and the app decides when to spend it.
 *
 * ⚠️ iOS HAS NO API FOR THIS. Safari never fires beforeinstallprompt and
 * offers no programmatic install, so the only honest thing is an instruction:
 * Share, then Add to Home Screen. Which is also the ONLY way an iPhone can
 * ever receive a push from this app.
 */

let deferred: any = null;

export function watchForInstall(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Held, not shown. Chrome will not re-fire it later if we let it through.
    e.preventDefault();
    deferred = e;
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
  });
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // Safari's own flag, which is the only signal iOS gives.
    (navigator as any).standalone === true
  );
}

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as a Mac; a touch point count separates them.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

/** Android and desktop Chrome can be prompted; iOS cannot. */
export function canPromptInstall(): boolean {
  return deferred !== null;
}

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferred) return "unavailable";
  deferred.prompt();
  const { outcome } = await deferred.userChoice;
  // Single use, whatever they chose.
  deferred = null;
  return outcome === "accepted" ? "accepted" : "dismissed";
}

/** Register the worker, quietly. A failure here must not break the app. */
export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // No worker means no offline and no push. Everything else still works,
      // so there is nothing worth interrupting somebody about.
    });
  });
}

/**
 * Empty everything the worker is holding.
 *
 * ⛔ Called on sign-out. The cache is per ORIGIN, not per user — a second
 * person signing in on the same phone must not read the first one's kitchen.
 */
export function clearOfflineData(): void {
  navigator.serviceWorker?.controller?.postMessage("tp-clear");
}
